import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Navbar } from "../../components/layout/Navbar";
import { Footer } from "../../components/layout/Footer";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { useCart } from "../../context/CartContext";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Heart, Package, ShoppingCart, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerFavorites = () => {
  const { getAuthHeaders } = useAuth();
  const { t, isRTL } = useLanguage();
  const { addItem } = useCart();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const response = await axios.get(`${API}/favorites`, {
          headers: getAuthHeaders(),
        });
        setFavorites(response.data);
      } catch (error) {
        console.error("Failed to fetch favorites:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchFavorites();
  }, [getAuthHeaders]);

  const removeFavorite = async (productId) => {
    try {
      await axios.delete(`${API}/favorites/${productId}`, {
        headers: getAuthHeaders(),
      });
      setFavorites((prev) => prev.filter((p) => p.id !== productId));
      toast.success(t("removedFromFavorites"));
    } catch (error) {
      toast.error(t("failedToRemoveFavorite"));
    }
  };

  const handleAddToCart = (product) => {
    addItem(product, 1);
    toast.success(`${product.name} ${t("addedToCartMsg")}`);
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Navbar />
      
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-8" data-testid="customer-favorites">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">{t("favorites")}</h1>
          <p className="text-muted-foreground">{t("savedForLater")}</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-80 bg-muted animate-pulse rounded-xl" />
            ))}
          </div>
        ) : favorites.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-16 text-center">
              <Heart className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading font-semibold text-lg mb-2">{t("noFavoritesYet")}</h3>
              <p className="text-muted-foreground mb-6">
                {t("noFavoritesDesc")}
              </p>
              <Link to="/products">
                <Button className="bg-[#1a365d]">{t("browseProducts")}</Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {favorites.map((product) => (
              <Card key={product.id} className="group overflow-hidden hover:shadow-xl transition-all duration-300">
                <div className="aspect-square bg-gradient-to-br from-slate-100 to-slate-50 relative overflow-hidden">
                  {product.images?.[0] ? (
                    <img
                      src={product.images[0]}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-16 h-16 text-slate-300" />
                    </div>
                  )}
                  
                  {product.discount > 0 && (
                    <Badge className={`absolute top-3 ${isRTL ? 'right-3' : 'left-3'} bg-red-500 text-white`}>
                      -{product.discount}%
                    </Badge>
                  )}
                  
                  <button
                    onClick={() => removeFavorite(product.id)}
                    className={`absolute top-3 ${isRTL ? 'left-3' : 'right-3'} p-2 bg-white rounded-full shadow-md hover:bg-red-50 transition-colors`}
                    data-testid={`remove-favorite-${product.id}`}
                  >
                    <Trash2 className="w-5 h-5 text-red-500" />
                  </button>
                </div>
                
                <CardContent className="p-6">
                  <p className="text-xs text-[#d4af37] font-medium uppercase tracking-wide mb-1">
                    {product.brand || "Bahr Al Rimal"}
                  </p>
                  <Link to={`/products/${product.id}`}>
                    <h3 className="font-heading font-semibold text-lg mb-2 group-hover:text-[#1a365d] transition-colors line-clamp-2">
                      {product.name}
                    </h3>
                  </Link>
                  
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      {product.discount > 0 ? (
                        <>
                          <span className="text-xl font-bold text-[#1a365d]">
                            {(product.sale_price * (1 - product.discount / 100)).toFixed(2)} {t("lyd")}
                          </span>
                          <span className={`text-sm text-muted-foreground line-through ${isRTL ? 'mr-2' : 'ml-2'}`}>
                            {product.sale_price} {t("lyd")}
                          </span>
                        </>
                      ) : (
                        <span className="text-xl font-bold text-[#1a365d]">
                          {product.sale_price} {t("lyd")}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <Button
                    onClick={() => handleAddToCart(product)}
                    className="w-full bg-[#1a365d] hover:bg-[#1a365d]/90"
                    data-testid={`add-to-cart-${product.id}`}
                  >
                    <ShoppingCart className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                    {t("addToCart")}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default CustomerFavorites;
